import { useCallback } from "react";
import { Box, Grid2 as Grid, Paper, Typography } from "@mui/material";
import { BarChart, CreditCard, List as ListIcon, Preview, SpaceDashboard } from '@mui/icons-material';
import { Link } from "react-router-dom";

import onboarding from "../../../../assets/onboarding.png";

type OnboardingProps = {
    type: "advertiser" | "admin" | "scout";
}

const OnboardingWidget = ({ type }: OnboardingProps) => {
    const getInfoByType = useCallback(() => {
        switch(type) {
            case "advertiser":
                return <>
                    <Typography variant="h5">Introducing your Statrat Advertiser Dashboard</Typography>
                    <Typography>
                        With Statrat, you can reach baseball players around the world and target the
                        exact demographic you're looking for to gain new users today! Here's a quick look
                        at what you can do:
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <SpaceDashboard />
                        <Typography>Dashboard</Typography>
                    </Box>
                    <Typography>
                        Your landing page that will give you a preview at your ongoing ad campaigns.
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <ListIcon />
                        <Typography>Manage Campaigns</Typography>
                    </Box>
                    <Typography>
                        Create new ad campaigns and manage their current status.
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <BarChart />
                        <Typography>Analytics</Typography>
                    </Box>
                    <Typography>
                        View various stats about your campaign and get a breakdown on how they are doing.
                    </Typography>
                    <Typography variant="h5" pt={2}>Getting Started</Typography>
                    <Typography>
                        To get started, you'll first need to <Link to="settings/billing">add a payment method</Link> to
                        your account so we can get you verified. We will never charge anything for adding a card, you can
                        get more details on our billing policy at our <Link to="/settings/help">FAQ</Link>.
                    </Typography>
                </>
            case "admin":
                return <>
                    <Typography variant="h5">Introducing your Statrat Admin Dashboard</Typography>
                    <Typography>
                        As admin, you are responsible for responding to new ad campaign requests, as well as
                        manage various data such as templates and payment transactions.
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <ListIcon />
                        <Typography>Manage Campaigns</Typography>
                    </Box>
                    <Typography>
                        View all currently ongoing ad campaigns and approve or reject campaigns that are awaiting a response.
                        If an ad doesn't meet expectations, be sure to include a detailed reason why. 
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <Preview />
                        <Typography>Manage Templates</Typography>
                    </Box>
                    <Typography>
                        Create new templates for users to customize their ads with and achieve their ideal layout.
                        Use the sample layout as a starting point to design templates that give users full control of what
                        content they want to appear in their ad. Information on what format templates should follow is
                        also available.
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <CreditCard />
                        <Typography>View Payments</Typography>
                    </Box>
                    <Typography>
                        Manage payments made by users, ensuring transactions are being billed correctly and are in sync
                        between the backend database and Stripe database.
                    </Typography>
                </>
            case "scout":
                return <>
                    <Typography variant="h5">Introducing your Statrat Scout Dashboard</Typography>
                </>
        }
    }, [type]);

    return (
        <Grid size={12}>
            <Paper elevation={5} sx={{ display: 'flex', minHeight: 230, gap: 1 }}>
                <Box flex="0 0 30%" overflow="hidden" position="relative">
                    <img 
                        src={onboarding}
                        alt="cover-image"
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover',
                            filter: 'blur(2px)'
                        }}
                    />
                </Box>
                <Box p={2} width="80%" display="flex" flexDirection="column" gap={2}>
                    {getInfoByType()}
                </Box>
            </Paper>
        </Grid>
    )
}

export default OnboardingWidget;
